import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select-legacy';
import moment from 'moment-timezone';
import DatePicker from 'react-datepicker';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import clsx from 'clsx';
import _ from 'lodash';
import { useMutationState } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { authHeaders } from 'actions';
import Routes from 'utils/routes.js.erb';

const diffBettweenDateAndNow = (date) => {
  const now = moment();
  const diff = now.diff(date, 'months');
  return diff;
}

const toDate = (date) => {
  const d = moment.utc(date).format('YYYY-MM-DD')
  return new Date(d.split('-')[0], d.split('-')[1] - 1, d.split('-')[2]);
}

const getSponsorNames = (cohortIds, sponsors) => {
  const sponsorNames = [];
  (cohortIds ?? []).forEach((cohortId) => {
    const list = sponsors[cohortId.toString()];
    (list ?? []).forEach((sponsor) => {
      if (!sponsorNames.includes(sponsor.title)) {
        sponsorNames.push(sponsor.title);
      }
    });
  });

  return sponsorNames;
};

const getCohorts = (admin_cohorts, player_cohorts) => {
  const admin_cohort_ids = admin_cohorts.map(c => c.value);
  const fixed_cohorts = player_cohorts.filter(c => !admin_cohort_ids.includes(c.id)).map(c => ({ value: c.id, label: c.name, clearableValue: false }));
  return admin_cohorts.concat(fixed_cohorts);
}

const Sponsor = ({ cohort_ids, index, errors, register }) => {
  const sponsorsQuery = useQuery({
    queryKey: ['administration', 'sponsor', cohort_ids],
    queryFn: ({ signal }) => axios.get(
      Routes.sponsors_api_dashboard_administration_filters_path({
        cohort_ids,
      }),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
  })

  const sponsorNames = getSponsorNames(cohort_ids, sponsorsQuery.data ?? []);

  return (<>
    {sponsorNames.length > 0 && (
      <p>
        <input className={clsx({'u-chk--error': _.get(errors, `players_attributes[${index}].sponsors_accepeted`)})} id={`players_attributes[${index}].sponsors_accepeted`} type="checkbox" {...register(`players_attributes[${index}].sponsors_accepeted`)} />
        <label className={clsx('u-txt--label', { 'u-txt--error': _.get(errors, `players_attributes[${index}].sponsors_accepeted`) })} htmlFor={`players_attributes[${index}].sponsors_accepeted`}>{'   '} I agree that this Child&apos;s data will be available for the following organizations:</label><br />
        <ul>
          {sponsorNames.map((sponsorName, i) => (<li className='u-txt--label' key={i}>{sponsorName}</li>))}
        </ul>
      </p>
    )}
    {_.get(errors, `players_attributes[${index}].utah_consent`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].utah_consent`).message}</span>)}
  </>)
}

Sponsor.propTypes = {
  cohort_ids: PropTypes.array,
  index: PropTypes.number,
  errors: PropTypes.object,
  register: PropTypes.func,
}

const AccountInputs = ({
  onResetPassword,
  onRequestToDeleteAccount,
  onRequestToDeletePlayer,
}) => {
  const state = useMutationState({
    filters: { mutationkey: ['administration', 'accounts', 'save'] },
    select: (mutation) => mutation.state,
  })

  const accesses = useQuery({
    queryKey: ['administration', 'organization', 'my-accesses'],
    queryFn: ({ signal }) => axios.get(
      Routes.api_dashboard_administration_organizations_my_accesses_path(),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
  })

  const customIds = useQuery({
    queryKey: ['administration', 'accounts', 'customIds'],
    queryFn: ({ signal }) => axios.get(
      Routes.custom_ids_api_dashboard_administration_accounts_path(),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
  })

  const { watch, register, control, formState: { errors } } = useFormContext()
  const { fields, append, remove } = useFieldArray(
    {
      control,
      name: 'players_attributes',
      keyName: 'fakeid',
    }
  );

  const latest = state[state.length - 1]
  const isPending = latest && latest.status === 'pending'
  const accountId = watch('id')
  const isNewAccount = !accountId
  const newChild = {
    name: null,
    gender: null,
    race: null,
    birthday: null,
    weeks_premature: 0,
    cohort_ids: [],
    sponsors_accepeted: false,
    custom_ids: customIds.data ?? [],
    _destroy: false,
  };

  if (fields.length === 0 && !customIds.isPending) {
    append(newChild)
  }

  return (
    <>
      <div className='o-card__wrap o-admin-card'>
        <section className='column o-card cs-bg--light'>
          {isNewAccount && (
            <div className='row'>
              <div className='column small-12'>
                <p className='u-txt--label'>{'By adding this account and/or child profile, you acknowledge you have all the necessary consent from the parent or caregiver to create this account on behalf of the family. If you create a new parent/caregiver account, an email will be sent to the parent/caregiver to activate their account. If you create a new child within a family\'s existing account, the child will appear in their Sparkler app.'}</p>
              </div>
            </div>
          )}
          <div className='row'>
            <div className='column small-8'>
              <h3><b>Parent/Caregiver</b></h3>
            </div>
            <div className='column small-4' style={{ textAlign: 'right' }}>
              {onResetPassword && !isNewAccount && (
                <span className='o-sp-button o-sp-button--update action-btn' onClick={() => {
                  onResetPassword(accountId);
                }}>
                  <i className='fa fa-envelope'></i>&nbsp;Reset Password
                </span>
              )}
              {onRequestToDeleteAccount && !isNewAccount && (
                <span className='o-sp-button o-sp-button--update action-btn u-margin-top--small' onClick={() => {
                  onRequestToDeleteAccount(accountId);
                }}>
                  <i className='fa fa-trash'></i>&nbsp;Request to Delete
                </span>
              )}
            </div>
          </div>
          <div className='u-margin-top--base'>
            <input type='hidden' {...register('partner_id')} />
            <p>
              <span className='u-txt--label'>First Name</span><br />
              <input type='text' {...register('first_name')} aria-invalid={errors.first_name ? true : false} />
              {errors.first_name && (<span className='u-txt--error'>{errors.first_name.message}</span>)}
            </p>
            <p>
              <span className='u-txt--label'>Last Name</span><br />
              <input type='text' {...register('last_name')} aria-invalid={errors.last_name ? true : false} />
              {errors.last_name && (<span className='u-txt--error'>{errors.last_name.message}</span>)}
            </p>
            <p>
              <span className='u-txt--label'>Email</span><br />
              <input type='email' {...register('email')} aria-invalid={errors.email ? true : false} />
              {errors.email && (<span className='u-txt--error'>{errors.email.message}</span>)}
            </p>
            <p>
              <span className='u-txt--label'>Home address</span><br />
              <input type='text' {...register('address')} aria-invalid={errors.address ? true : false} />
              {errors.address && (<span className='u-txt--error'>{errors.address.message}</span>)}
            </p>
            <p>
              <span className='u-txt--label'>ZIP Code</span><br />
              <Controller
                name="zip"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputMask value={value} onChange={onChange} mask="99999">
                    {(inputProps) => (
                      <input
                        type="text"
                        aria-invalid={errors.zip ? true : false}
                        {...inputProps}
                      />
                    )}
                  </InputMask>
                )}
              />
              {errors.zip && (<span className='u-txt--error'>{errors.zip.message}</span>)}
            </p>
            <p>
              <span className='u-txt--label'>Phone number</span><br />
              <input type='text' {...register('phone')} aria-invalid={errors.phone ? true : false} />
              {errors.phone && (<span className='u-txt--error'>{errors.phone.message}</span>)}
            </p>
          </div>
        </section>
      </div>

      <div className='o-card__wrap o-admin-card'>
        <section className='column o-card cs-bg--light'>
          <div className='row'>
            <div className='column small-6'>
              <h3><b>Children</b></h3>
            </div>
          </div>
          <div className='row'>
            <div className="column">
              {fields.map((player, index) => {
                const birthday = watch(`players_attributes[${index}].birthday`);
                const cohort_ids = watch(`players_attributes[${index}].cohort_ids`);
                const cohorts = watch(`players_attributes[${index}].cohorts`);
                const childId = watch(`players_attributes[${index}].id`);
                const isNewChild = !childId;

                if (player._destroy) return null;

                return (
                  <section style={{position: 'relative'}} key={player.fakeid} className={clsx('column o-card cs-bg--light')}>
                    <div className='row'>
                      <div className="column" style={{textAlign: 'right'}}>
                        {isNewChild && fields.filter(f => !f._destroy).length > 1 && (

                          <span className='o-sp-button o-sp-button--update action-btn' onClick={() => {
                            remove(index)
                          }}>
                            <span><i className='fa fa-trash'></i>&nbsp;Remove</span>
                          </span>
                        )}
                        {onRequestToDeletePlayer && !isNewChild && (
                          <span className='o-sp-button o-sp-button--update action-btn' onClick={() => {
                            onRequestToDeletePlayer(accountId, childId);
                          }}>
                            <i className='fa fa-trash'></i>&nbsp;Request to Delete
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='row u-margin-top--base'>
                      <div className="column">
                        <input type="hidden" {...register(`players_attributes[${index}].id`)} />
                        <input type="hidden" {...register(`players_attributes[${index}]._destroy`)} />
                        <p>
                          <span className='u-txt--label'>First Name</span><br />
                          <input type='text' {...register(`players_attributes[${index}].first_name`)} aria-invalid={_.get(errors, `players_attributes[${index}].first_name`) ? true : false} />
                          {_.get(errors, `players_attributes[${index}].first_name`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].first_name`).message}</span>)}
                        </p>
                        <p>
                          <span className='u-txt--label'>Last Name</span><br />
                          <input type='text' {...register(`players_attributes[${index}].last_name`)} aria-invalid={_.get(errors, `players_attributes[${index}].last_name`) ? true : false} />
                          {_.get(errors, `players_attributes[${index}].last_name`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].last_name`).message}</span>)}
                        </p>
                        <p>
                          <span className='u-txt--label'>Gender</span><br />
                          <Controller
                            name={`players_attributes[${index}].gender`}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                className={clsx('o-select o-select--optgroup', { 'o-select--error': _.get(errors, `players_attributes[${index}].gender`) })}
                                value={value}
                                onChange={onChange}
                                options={
                                  [
                                    { value: 'male', label: 'Male' },
                                    { value: 'female', label: 'Female' },
                                    { value: 'other', label: 'Other' },
                                  ]
                                }
                                simpleValue={true} />
                            )}
                          />
                          {_.get(errors, `players_attributes[${index}].gender`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].gender`).message}</span>)}
                        </p>
                        <p>
                          <span className='u-txt--label'>Date of Birth or Due date</span><br />
                          <Controller
                            name={`players_attributes[${index}].birthday`}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <DatePicker
                                className={clsx({ 'react-datepicker-wrapper--error': _.get(errors, `players_attributes[${index}].birthday`) })}
                                selected={value && toDate(value)}
                                onChange={onChange}
                              />
                            )}
                          />
                          {_.get(errors, `players_attributes[${index}].birthday`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].birthday`).message}</span>)}
                        </p>
                        {diffBettweenDateAndNow(birthday) < 24 && (<p>
                          <span className='u-txt--label'>Weeks Premature</span><br />
                          <input
                            type='number'
                            step='1'
                            {...register(`players_attributes[${index}].weeks_premature`, {
                              min: { value: 0, message: 'Please enter a positive number' },
                              max: { value: 20, message: 'Prematurity cannot exceed 20 weeks' },
                              valueAsNumber: true,
                              onChange: (e) => {
                                const value = parseInt(e.target.value);
                                if (value > 20 || value < 0) {
                                  e.target.setCustomValidity('');
                                }
                              }
                            })}
                            onInput={(e) => e.target.setCustomValidity('')}
                            aria-invalid={_.get(errors, `players_attributes[${index}].weeks_premature`) ? true : false}
                          />
                          {_.get(errors, `players_attributes[${index}].weeks_premature`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].weeks_premature`).message}</span>)}
                        </p>)}
                        <p>
                          <span className='u-txt--label'>Race</span><br />
                          <Controller
                            name={`players_attributes[${index}].race`}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                className={clsx('o-select o-select--optgroup', { 'o-select--error': _.get(errors, `players_attributes[${index}].race`) })}
                                value={value}
                                onChange={onChange}
                                options={
                                  [
                                    { value: 'caucasian', label: 'Caucasian' },
                                    { value: 'african_american', label: 'African American' },
                                    { value: 'hispanic_or_latino', label: 'Hispanic or Latino' },
                                    { value: 'asian', label: 'Asian' },
                                    { value: 'middle_eastern', label: 'Middle-Eastern' },
                                    { value: 'native_american', label: 'Native American' },
                                    { value: 'hawaiian_pacific_islander', label: 'Hawaiian Pacific Islander' },
                                    { value: 'other', label: 'Other' },
                                    { value: 'unknown', label: 'Unknown' },
                                  ]
                                }
                                simpleValue={true} />
                            )}
                          />
                          {_.get(errors, `players_attributes[${index}].race`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].race`).message}</span>)}
                        </p>
                        {(player.custom_ids ?? []).map((customId, i) => {
                          return(
                            <p key={customId.handle}>
                              <span className='u-txt--label'>{customId.label}</span><br />
                              <input type='hidden' {...register(`players_attributes[${index}].custom_ids[${i}].handle`)} />
                              <input type='text' {...register(`players_attributes[${index}].custom_ids[${i}].value`)} aria-invalid={_.get(errors, `players_attributes[${index}].custom_ids[${i}].value`) ? true : false} />
                              {_.get(errors, `players_attributes[${index}].custom_ids[${i}].value`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].custom_ids[${i}].value`).message}</span>)}
                            </p>
                          )
                        })}
                        <p>
                          <span className='u-txt--label'>Cohorts</span><br />
                          <Controller
                            name={`players_attributes[${index}].cohort_ids`}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                className={clsx('o-select o-select--optgroup', { 'o-select--error': _.get(errors, `players_attributes[${index}].cohort_ids`) })}
                                multi={true}
                                value={value}
                                clearable={false}
                                onChange={(value) => {
                                  onChange(value.split(','));
                                }}
                                options={getCohorts(accesses.data?.cohorts ?? [], cohorts ?? [])}
                                simpleValue={true}
                              />
                            )}
                          />
                          {_.get(errors, `players_attributes[${index}].cohort_ids`) && (<span className='u-txt--error'>{_.get(errors, `players_attributes[${index}].cohort_ids`).message}</span>)}
                        </p>
                        <p>
                          <Sponsor cohort_ids={cohort_ids} index={index} errors={errors} register={register} />
                        </p>
                      </div>
                    </div>
                    {isNewChild && (
                      <div className='row'>
                        <div className='column small-12'>
                          <p className='u-txt--label'>{'By adding this account and/or child profile, you acknowledge you have all the necessary consent from the parent or caregiver to create this account on behalf of the family. If you create a new parent/caregiver account, an email will be sent to the parent/caregiver to activate their account. If you create a new child within a family\'s existing account, the child will appear in their Sparkler app.'}</p>
                        </div>
                      </div>
                    )}
                  </section>
                )
              })}
            </div>
          </div>
          <div className='row u-margin-top--base'>
            <div style={{ textAlign: 'center' }} className='column small-12'>
              <span className='o-sp-button o-sp-button--update action-btn' onClick={() => append(newChild)}>
                <i className='fa fa-plus'></i>&nbsp;Add Child
              </span>
            </div>
          </div>
        </section>
      </div>
      <div className='row u-margin-top--large u-margin-bottom--large'>
        <div className='column small-12'>
          <input type='submit' className='o-sp-button o-sp-button--update o-sp-button--primary' value="Save" disabled={isPending} />
        </div>
      </div>
    </>
  )
}

AccountInputs.propTypes = {
  auth: PropTypes.object,
  onResetPassword: PropTypes.func,
  onRequestToDeleteAccount: PropTypes.func,
  onRequestToDeletePlayer: PropTypes.func,
}

export default AccountInputs;
